import classNames from 'classnames';
import { memo, useMemo, useState } from 'react';

import { type PublicClip } from '@air/api';
import { type BaseCustomField, type Clip } from '@air/api/types';
import { getFilename } from '@air/component-file-extension-preview';
import { Comment as CommentIcon, Ellipsis as EllipsisHorizontalIcon, Stack as StackIcon } from '@air/next-icons';

import { CardAction } from '~/components/CardAction';
import { AssetGalleryCardMetaDetails } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardMetaDetails';
import { AssetGalleryCardProcessing } from '~/components/Gallery/AssetGalleryCard/AssetGalleryCardProcessing';
import { CustomFieldsDisplay } from '~/components/Gallery/CustomFieldsDisplay';
import { FileGalleryCardMenu } from '~/components/Gallery/FileGalleryCard/FileGalleryCardMenu';
import { GalleryCard, type GalleryCardProps } from '~/components/Gallery/GalleryCard/GalleryCard';
import { GalleryCardCustomFieldsAction } from '~/components/Gallery/GalleryCardCustomFieldsAction';
import { GalleryItemType } from '~/components/Gallery/types';
import { getAssetCustomFields } from '~/components/Gallery/utils';
import { RiveContainer } from '~/components/Shared/RiveContainer/RiveContainer';
import { ASSET_CARD, ASSET_CARD_SUB_MENU_BUTTON, ASSET_CARD_TITLE } from '~/constants/testIDs';
import { visibleCustomFieldsViewOptionsSelector } from '~/store/configViews/selectors';
import { type SelectableGalleryFileItem } from '~/store/selectedItems/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type RiveGalleryCardProps<C> = {
  cardWidth: number;
  cardHeight: number;
  file: C;
  onCustomFieldsClick: () => void;
  onDiscussionsClick?: () => void;
  onVersionsClick?: () => void;
  showVersionsIndicator?: boolean;
  onFileClick: () => void;
  onFileTitleCmdClick?: () => void;
  canViewCustomFields: boolean;
  allCustomFields: BaseCustomField[] | undefined;
} & Pick<
  GalleryCardProps<SelectableGalleryFileItem>,
  'getButtonClassName' | 'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable'
>;

const _RiveGalleryCard = <C extends Clip | PublicClip>({
  file,
  getItemMenuOptions,
  getSelectionMenuOptions,
  isSelectable = true,
  onCustomFieldsClick,
  onDiscussionsClick,
  onVersionsClick,
  showVersionsIndicator = false,
  onFileClick,
  onFileTitleCmdClick,
  canViewCustomFields,
  allCustomFields = [],
  cardWidth,
  cardHeight,
  getButtonClassName,
}: RiveGalleryCardProps<C>) => {
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);
  const [isRiveReady, setIsRiveReady] = useState(false);
  const [hasRiveError, setHasRiveError] = useState(false);

  const src = file.assets?.original;

  // Only show errors for files that have explicitly failed processing
  // nonTranscodable files might still be propagating to CDN, so keep showing processing
  const shouldShowError = file.status === 'failed';

  const customFields = useMemo(
    () => getAssetCustomFields({ allCustomFields, clip: file, visibleCustomFields }),
    [allCustomFields, file, visibleCustomFields],
  );

  const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;
  const titleLabel = getFilename(file);

  const popoverCustomFields = useMemo(
    () =>
      file?.customFields?.filter((customField) => {
        return allCustomFields.some((cf) => cf.id === customField.id);
      }),
    [file, allCustomFields],
  );

  return (
    <GalleryCard
      testId={ASSET_CARD}
      title={titleLabel}
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      item={{
        id: file.id,
        item: file,
        type: GalleryItemType.file,
      }}
      onClick={onFileClick}
      isSelectable={isSelectable}
      getButtonClassName={getButtonClassName}
    >
      {({ setIsMenuOpen, isHovering }) => (
        <div className="pointer-events-none relative flex size-full flex-col content-between rounded-4 border border-grey-4 bg-grey-1">
          <div className="relative size-full">
            {/* Show processing or error based on file status */}
            {!isRiveReady &&
              (hasRiveError && shouldShowError ? (
                // Only show error if file explicitly failed processing
                <div className="absolute inset-0 flex items-center justify-center rounded-4 bg-grey-2">
                  <div className="text-12 text-red-9">Failed to load animation</div>
                </div>
              ) : (
                // Show processing for all other states (including nonTranscodable with CDN delays)
                <AssetGalleryCardProcessing ext={file.ext} className="absolute inset-0 rounded-4" />
              ))}

            {/* Render RiveContainer (might be hidden by overlays above) */}
            {!!src && (
              <RiveContainer
                src={src}
                className="size-full rounded-4"
                resizeDependencies={[cardWidth, cardHeight]}
                onRiveReady={() => {
                  setIsRiveReady(true);
                  setHasRiveError(false);
                }}
                onLoadError={() => {
                  // Set error state so we can show error message if processing is complete
                  setHasRiveError(true);
                }}
              />
            )}

            {/* Gradient overlay with title (shows on hover) */}
            <div
              className={classNames(
                'absolute inset-x-0 bottom-0 flex h-[80px] flex-col justify-end rounded-4 rounded-t-none bg-gradient-to-b from-black/0 to-black/90 px-2 pt-1 pb-2 transition-opacity',
                isHovering ? 'opacity-100' : 'opacity-0',
              )}
            >
              <AssetGalleryCardMetaDetails
                hasDarkBackground
                clip={file}
                title={titleLabel}
                onTitleClick={onFileClick}
                onTitleCmdClick={onFileTitleCmdClick}
                width={cardWidth - 16}
                titleTestId={ASSET_CARD_TITLE}
              />
            </div>

            {/* Top controls */}
            <div className="pointer-events-none absolute top-2 left-2 flex gap-1">
              {canViewCustomFields && !!popoverCustomFields?.length && (
                <GalleryCardCustomFieldsAction customFields={popoverCustomFields} onClick={onCustomFieldsClick} />
              )}
              {showVersionsIndicator && !!onVersionsClick && (
                <CardAction
                  data-testid="CARD_VERSIONS_INDICATOR"
                  icon={<StackIcon className="size-4" />}
                  onClick={onVersionsClick}
                />
              )}
              {!!onDiscussionsClick && !!file.openCommentCount && (
                <CardAction
                  data-testid="CARD_DISCUSSIONS_INDICATOR"
                  icon={<CommentIcon className="size-4" />}
                  onClick={onDiscussionsClick}
                  text={file.openCommentCount}
                />
              )}
            </div>

            {/* Menu (shows on hover) */}
            {isHovering && (
              <div className="pointer-events-none absolute top-2 right-2">
                <FileGalleryCardMenu
                  clip={file}
                  getItemMenuOptions={getItemMenuOptions}
                  getSelectionMenuOptions={getSelectionMenuOptions}
                  setIsMenuOpen={setIsMenuOpen}
                  trigger={
                    <CardAction
                      color="light"
                      data-testid={ASSET_CARD_SUB_MENU_BUTTON}
                      icon={<EllipsisHorizontalIcon className="size-4" />}
                    />
                  }
                />
              </div>
            )}

            {/* Custom fields */}
            {shouldShowCustomFields && (
              <div className="absolute bottom-20 left-2">
                <CustomFieldsDisplay className="px-1" customFields={customFields} onClick={onCustomFieldsClick} />
              </div>
            )}
          </div>
        </div>
      )}
    </GalleryCard>
  );
};

export const RiveGalleryCard = memo(_RiveGalleryCard) as typeof _RiveGalleryCard;
